/**
 * navigation/appShell.tsx — the signed-in SK Queue admin shell.
 * Wide screens: docked sidebar + topbar + active surface.
 * Narrow screens: the sidebar collapses to a hamburger-triggered overlay drawer
 * (so every admin surface stays reachable on a phone). Public surfaces (Guest,
 * Now Serving) don't use this shell — they render standalone from rootNavigator.
 */

import React, { useEffect, useState } from 'react';
import { View, Platform, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useTheme } from '../context/themeContext';
import Sidebar from '../shell/sidebar';
import Topbar from '../shell/topbar';
import { ActiveSurface } from './surfaceScreens';

export default function AppShell() {
  const { theme } = useTheme();
  const { colors } = theme;
  const { width } = useWindowDimensions();
  const isMobile = width < 820;

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Auto-close the drawer when growing back to desktop.
  useEffect(() => { if (!isMobile && drawerOpen) setDrawerOpen(false); }, [isMobile, drawerOpen]);

  return (
    <View style={{
      // Fills #root (a flex column on web — see web/index.html), so this row
      // gets the full viewport height via flex:1, no 100vh needed.
      // overflow:hidden keeps the chrome put; each surface scrolls its own regions.
      flex: 1, flexDirection: 'row', minHeight: 0,
      ...(Platform.OS === 'web' ? { overflow: 'hidden' as any } : {}),
    }}>
      {/* Docked sidebar — wide screens only. */}
      {!isMobile && <Sidebar />}

      <View style={{ flex: 1, flexDirection: 'column', minWidth: 0, minHeight: 0, ...(Platform.OS === 'web' ? { overflow: 'hidden' as any } : {}) }}>
        <Topbar showMenu={isMobile} onMenu={() => setDrawerOpen(true)} />
        {/* Surface area: flex:1 + minHeight:0 so a surface fills it and any inner
            ScrollView can shrink-and-scroll instead of pushing this taller. */}
        <View style={{ flex: 1, minHeight: 0, backgroundColor: colors.paper }}>
          <ActiveSurface />
        </View>
      </View>

      {/* Mobile overlay drawer — sidebar slides over the content; tapping the
          scrim or selecting a surface closes it. */}
      {isMobile && drawerOpen && (
        <View style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, flexDirection: 'row',
          ...(Platform.OS === 'web' ? { zIndex: 100 as any } : {}),
        }}>
          <Sidebar onNavigate={() => setDrawerOpen(false)} />
          <TouchableOpacity
            style={{ flex: 1, backgroundColor: 'rgba(8,9,10,0.45)' }}
            activeOpacity={1}
            onPress={() => setDrawerOpen(false)}
            accessibilityLabel="Close menu"
          />
        </View>
      )}
    </View>
  );
}
